import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import type { PropertyOption } from 'lumen/components/settings/users/property-select';
import type { UserChange } from 'lumen/components/settings/users/tables/users-table';
import type AggregatedUser from 'lumen/models/aggregated-user';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type Userhotelrelationship from 'lumen/models/userhotelrelationship';
import type {
  RouteParams,
  UsersModel,
} from 'lumen/routes/hotel/settings/users';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import { ACCESS_RIGHTS, PRODUCTS } from 'lumen/utils/constants/users-constants';
import { timeout } from 'lumen/utils/waiters';

type SortableColumn = 'name' | 'role';

export default class HotelSettingsUsersController extends Controller {
  declare model: UsersModel;

  queryParams = [
    'sortBy',
    'sortDirection',
    'search',
    'role',
    'product',
    'property',
  ];

  @service declare access: Services['access'];
  @service('ajax') declare ajaxService: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked accumulatedAggregatedUsers: AggregatedUser[] = [];
  @tracked accumulatedUsers: SubscriptionUser[] = [];
  @tracked currentPage = 1;
  @tracked isInviteModalVisible = false;
  @tracked product: string | null = null;
  @tracked property: string | null = null;
  @tracked role: string | null = null;
  @tracked search: string | null = null;
  @tracked searchInputValue: string | null = null;
  @tracked selectedPropertyOption: PropertyOption | null = null;
  @tracked sortBy: SortableColumn = 'name';
  @tracked sortDirection: 'asc' | 'desc' = 'asc';
  @tracked totalRecords = 0;

  debouncedSearchTask = task({ restartable: true }, async (value: string) => {
    await timeout();

    const trimmed = value.trim();
    if (!trimmed) {
      this.search = null;
    } else {
      this.search = trimmed
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean)
        .join(',');
    }
  });

  loadMoreTask = task({ drop: true }, async (): Promise<void> => {
    if (
      !this.hasMore ||
      this.model.loadUsersTask.isRunning ||
      this.model.loadAggregatedUsersTask.isRunning
    ) {
      return;
    }

    const params = this.buildLoadParams(this.currentPage + 1);

    if (this.isAllProperties) {
      const result = await this.model.loadAggregatedUsersTask.perform(params);

      if (result) {
        this.accumulatedAggregatedUsers = [
          ...this.accumulatedAggregatedUsers,
          ...result.users,
        ];
        this.totalRecords = result.meta.results_count;
        this.currentPage = this.currentPage + 1;
      } else {
        this.totalRecords = this.accumulatedAggregatedUsers.length;
      }
      return;
    }

    const result = await this.model.loadUsersTask.perform(params);

    if (result) {
      this.accumulatedUsers = [...this.accumulatedUsers, ...result.users];
      this.totalRecords = result.meta.results_count;
      this.currentPage = this.currentPage + 1;
    } else {
      this.totalRecords = this.accumulatedUsers.length;
    }
  });

  get displaySearchValue(): string {
    if (this.searchInputValue !== null) {
      return this.searchInputValue;
    }

    return this.search ?? '';
  }

  get hasMore(): boolean {
    const loadedCount = this.isAllProperties
      ? this.accumulatedAggregatedUsers.length
      : this.accumulatedUsers.length;
    return loadedCount < this.totalRecords;
  }

  get isAdmin(): boolean {
    if (this.access.isStaffUser) {
      return true;
    }

    const user = this.hotelAndMarketService.user;
    const hotelId = this.hotelAndMarketService.hotel?.id;

    if (!hotelId) {
      return false;
    }

    const relationships =
      user.userhotelrelationships as unknown as Userhotelrelationship[];
    const relationship = relationships.find(
      (rel) => rel.subscriptionId === Number(hotelId),
    );

    return relationship?.accessRight === ACCESS_RIGHTS.ADMIN;
  }

  get isAllProperties(): boolean {
    return this.property === null;
  }

  get isLoading(): boolean {
    return (
      (this.isAllProperties
        ? this.model.loadAggregatedUsersTask.isRunning
        : this.model.loadUsersTask.isRunning) ?? false
    );
  }

  get selectedProperties(): PropertyOption | null {
    return this.selectedPropertyOption;
  }

  get sorts(): { isAscending: boolean; valuePath: SortableColumn }[] {
    return [
      {
        valuePath: this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  buildLoadParams(page: number): RouteParams {
    return {
      page,
      sortBy: this.sortBy,
      sortDirection: this.sortDirection,
      search: this.search ?? undefined,
      role: this.role ?? undefined,
      product: this.product ?? undefined,
      property: this.property ?? undefined,
    };
  }

  async resetAndLoad(): Promise<void> {
    this.accumulatedAggregatedUsers = [];
    this.accumulatedUsers = [];
    this.currentPage = 1;
    this.totalRecords = 0;
    this.searchInputValue = this.search;

    const params = this.buildLoadParams(1);

    if (this.isAllProperties) {
      const result = await this.model.loadAggregatedUsersTask.perform(params);

      if (result) {
        this.accumulatedAggregatedUsers = result.users;
        this.totalRecords = result.meta.results_count;
      }
      return;
    }

    const result = await this.model.loadUsersTask.perform(params);

    if (result) {
      this.accumulatedUsers = result.users;
      this.totalRecords = result.meta.results_count;
    }
  }

  @action
  closeInviteModal(): void {
    this.isInviteModalVisible = false;
  }

  @action
  async deleteUser(user: SubscriptionUser): Promise<void> {
    const hotelName =
      this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name ?? '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.delete.confirm_title'),
      text: this.intl.t('settings.users.delete.confirm_message', {
        hotelName,
      }),
    });

    if (!confirmWasClicked) {
      return;
    }

    try {
      await user.destroyRecord();

      this.toast.success(this.intl.t('settings.users.delete.success'), {
        showIcon: true,
      });

      await this.resetAndLoad();
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.delete.failure'), {
        showIcon: true,
      });
    }
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath as SortableColumn;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    } else {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
  }

  @action
  openInviteModal(): void {
    this.isInviteModalVisible = true;
  }

  @action
  async saveChanges(changes: UserChange[]): Promise<boolean> {
    const confirmed = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.users.bulk_edit.confirm_title'),
      text: this.intl.t('settings.users.bulk_edit.confirm_text', {
        count: changes.length,
      }),
      unsafe_outsideClickCanCloseAlert: false,
    });

    if (!confirmed) {
      return false;
    }

    try {
      const subscriptionId = this.hotelAndMarketService.hotel.id;
      const users = changes.map((change) => {
        const update: {
          products?: string[];
          role?: string;
          user_id: string;
        } = { user_id: change.userId };

        if (change.newRole !== change.originalRole) {
          update.role = change.newRole;
        }

        const hasProductChanged =
          change.newRateAndMarketInsight !==
            change.originalRateAndMarketInsight ||
          change.newBiBenchmarking !== change.originalBiBenchmarking;

        if (hasProductChanged) {
          const products: string[] = [];
          if (change.newRateAndMarketInsight) {
            products.push(PRODUCTS.RATE_INSIGHT);
          }
          if (change.newBiBenchmarking) {
            products.push(PRODUCTS.BI_BENCHMARKING);
          }
          update.products = products;
        }

        return update;
      });

      await this.ajaxService.request(
        `${this.ajaxService.host}/${APIGATEWAY}/subscriptions/${subscriptionId}/users/bulk/`,
        {
          method: 'PATCH',
          data: JSON.stringify({ users }),
          contentType: 'application/json',
        },
      );

      this.toast.success(this.intl.t('settings.users.bulk_edit.success'), {
        showIcon: true,
      });

      await this.resetAndLoad();
      return true;
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.bulk_edit.failure'), {
        showIcon: true,
      });
      return false;
    }
  }

  @action
  updateProduct(product: string | null): void {
    this.product = product;
  }

  @action
  updateProperties(selectedOption: PropertyOption | null): void {
    this.property = selectedOption?.id ?? null;
    this.selectedPropertyOption = selectedOption;
  }

  @action
  updateRole(role: string | null): void {
    this.role = role;
  }

  @action
  updateSearch(event: Event): void {
    this.searchInputValue = (event.target as HTMLInputElement).value;
    void this.debouncedSearchTask.perform(this.searchInputValue);
  }
}
